/**
 * Terminal-level checks: does each cable end land on a terminal that exists,
 * and can that terminal carry what the cable carries?
 *
 * `Terminal.maxAmp` is read nowhere else in diagnostics. The engine only uses
 * it to clamp the per-endpoint resolve in `cable-current.ts`, and the
 * path-walked demand then overrides the clamp — so a 147 A inverter feed
 * routed through a 15 A switch sizes a 50 mm² cable and says nothing about
 * the switch (`everest`). A cable end that names a terminal its node does
 * not have is accepted by `migrate()` and simply absent from the graph, so
 * the part it feeds drops out of every calculation (`truck-24v-house`).
 */
import { isRatedPassThrough, seriesFuseTerminals } from "../terminal-fuse-limit";
import { getEffectiveCableFuse, OVERLOAD_TOLERANCE } from "../cable-current";
import { sustainedCableCurrent } from "../cable-current-evidence";
import type { DefLookup } from "../catalog";
import { computeCrankingCables } from "../cranking-circuit";
import { effectiveTerminals } from "../effective";
import { diagnosticLabel } from "../i18n/diagnostic-label";
import { indexNodesById } from "../node-tree";
import { isSenseTerminal } from "../sense-terminal";
import { DEFAULT_AUTO_SIZE_VDROP_TARGET } from "../settings/auto-size-vdrop-default";
import { findNode } from "../system-lookup";
import type { Issue, System, Terminal } from "../types";
import { makeDiagnosticCableSpecGetter } from "./sizing";
import { cableEndpointStatus } from "./cable-endpoints";

/** Past this multiple of the rating the terminal is a heater, not a contact. */
const CRITICAL_OVERLOAD_RATIO = 2;

const labelOf = (system: System, defLookup: DefLookup, nodeId: string): string => {
  const node = findNode(system, nodeId);
  if (!node) return nodeId;
  if (node.kind === "zone") return node.label ?? diagnosticLabel("diag.label.zone");
  const def = defLookup(node.definitionId, node.customDefinition);
  if (def?.renderVariant === "junction") return diagnosticLabel("diag.label.junction");
  return node.label ?? def?.shortName ?? def?.name ?? diagnosticLabel("diag.label.component");
};

/**
 * Terminals a cable may legally land on, per node. `undefined` when the node
 * has none to offer: missing, a zone, or a component whose definition cannot
 * be resolved (that last case is a definition defect, not a terminal one, and
 * is left to whichever check owns it).
 */
function terminalIndex(
  system: System,
  defLookup: DefLookup,
): (nodeId: string) => Map<string, Terminal> | undefined {
  const cache = new Map<string, Map<string, Terminal> | undefined>();
  return (nodeId) => {
    if (cache.has(nodeId)) return cache.get(nodeId);
    const node = findNode(system, nodeId);
    let index: Map<string, Terminal> | undefined;
    if (node?.kind === "component") {
      const def = defLookup(node.definitionId, node.customDefinition);
      if (def) {
        index = new Map(effectiveTerminals(def, node).map((term) => [term.id, term]));
      }
    }
    cache.set(nodeId, index);
    return index;
  };
}

const cableEnds = (cable: System["cables"][number]) =>
  [
    { nodeId: cable.fromNodeId, terminalId: cable.fromTerminalId },
    { nodeId: cable.toNodeId, terminalId: cable.toTerminalId },
  ] as const;

/**
 * A cable's continuous current exceeds the `maxAmp` of a terminal it lands on.
 * Sizing goes through the sanctioned getter so the current judged is the one
 * the canvas draws the conductor for.
 *
 * Cranking cables are skipped: `maxAmp` is a continuous rating and the
 * catalog carries no cranking figure, while manufacturers publish one that is
 * several times higher (Blue Sea m-Series 6006: 300 A continuous, 500 A for
 * 5 min, 900 A for 30 s cranking), so a 384 A starter pulse on a 300 A switch
 * is not a finding.
 * Signal terminals (a shunt's B+ sense lead) are skipped too: they carry no
 * load current, and the number the bank model hands a sense wire landed on a
 * battery post is the bank's share, not the wire's.
 */
export function detectTerminalOverloaded(
  system: System,
  defLookup: DefLookup,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): Issue[] {
  const specOf = makeDiagnosticCableSpecGetter(system, defLookup, autoSizeTargetPct);
  const terminalsOf = terminalIndex(system, defLookup);
  const issues: Issue[] = [];
  for (const cable of system.cables) {
    const spec = specOf(cable);
    // An AC run's `continuous` is the inlet rating it was sized for, not what
    // the appliance's own AC terminal carries.
    const sustained = sustainedCableCurrent(spec);
    if (sustained <= 0 || spec.isAc || spec.sourceAllocationDependent) continue;
    // A provisional total containing starting duty does not establish any
    // sustained-current value to compare with a continuous contact rating.
    if (spec.currentBasis === "provisional-fallback" && spec.sustainedCurrent == null &&
        (spec.isCranking || (spec.startingCurrent ?? 0) > 0)) continue;
    for (const end of cableEnds(cable)) {
      const node = findNode(system, end.nodeId);
      if (node?.kind !== "component") continue;
      const def = defLookup(node.definitionId, node.customDefinition);
      if (!def || !isRatedPassThrough(def)) continue;
      const terminal = terminalsOf(end.nodeId)?.get(end.terminalId);
      if (!terminal || terminal.maxAmp <= 0 || isSenseTerminal(def, terminal)) continue;
      if (sustained <= terminal.maxAmp * OVERLOAD_TOLERANCE) continue;
      const load = Math.round(sustained);
      const label = labelOf(system, defLookup, end.nodeId);
      const vars = { label, terminal: terminal.label, maxAmp: terminal.maxAmp, load };
      const provisional = spec.currentBasis === "provisional-fallback";
      issues.push({
        code: "terminal-overloaded",
        severity:
          !provisional && sustained > terminal.maxAmp * CRITICAL_OVERLOAD_RATIO
            ? "critical"
            : "warning",
        title: { key: provisional ? "diag.terminal-overloaded.title_provisional" : "diag.terminal-overloaded.title", vars },
        message: { key: provisional ? "diag.terminal-overloaded.message_provisional" : "diag.terminal-overloaded.message", vars },
        nodeIds: [end.nodeId],
        cableIds: [cable.id],
        fix: { key: provisional ? "diag.terminal-overloaded.fix_verify" : "diag.terminal-overloaded.fix", vars },
      });
    }
  }
  return issues;
}

/**
 * The fuse protecting a cable is rated above a series contact the cable lands
 * on — a 400 A fuse ahead of a 15 A rocker switch (`sea-truck-anderson`).
 * ABYC E-11.10.1.1 wants the OCPD to protect the conductor *and* what it
 * supplies; the engine honours the first half by lifting the wire to host the
 * fuse (E-11.10.1.2) and, before this check, said nothing about the second.
 * Between the fuse and the device any current up to the fuse rating persists,
 * so a contact rated below it is simply unprotected. The battery-switch
 * clause of ABYC E-11.10 asks the same the other way round: a switch's
 * continuous rating must not fall below its conductor's.
 *
 * Only *series* posts are judged — a terminal whose `direction` is `input` or
 * `output`: a switch, an inline holder's own posts, a shunt, a relay, an
 * isolator, a fuse block's feed post and its output slots (the slot's rating
 * is what the fuse in it may be). A `bidi` stud is a shared bus post: what
 * flows through it is the sum of its branches, each with its own fuse, and
 * the trunk fuse's rating is not what bounds it — `terminal-overloaded`
 * judges it on the current it actually carries. Measured over the 27
 * production fixtures, judging bus studs too raised 11 warnings on generic
 * 150 A bus bars under ordinary 175–400 A bank fuses (catamaran-24v,
 * contessa-26, shiva-wattcycle, …) and would have made the check noise on
 * every boat with a properly fused bank; the series rule keeps 13 findings on
 * 8 fixtures, each a contact rated below the fuse in front of it (a 15 A
 * rocker behind 20–400 A, a 300 A m-Series behind 350–400 A class-T, a
 * fuse block's 100 A feed post behind 125 A, an 8 A slot holding 20 A).
 *
 * Skipped: AC runs (the inlet breaker, E-11.10.2), cranking cables (no
 * OCPD, and the switch rating that matters is the intermittent one), sense
 * leads (no load current), and a rating inherited from a battery-post MRBF —
 * that fuse is sized for the bank's trunk and is `inline-fuse-needed`'s
 * finding, not this cable's. An inline holder's rating *is* this conductor's
 * OCPD and is judged.
 */
export function detectFuseExceedsTerminal(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const nodesById = indexNodesById(system.nodes);
  const cranking = computeCrankingCables(system, defLookup);
  const issues: Issue[] = [];
  for (const cable of system.cables) {
    const fuse = getEffectiveCableFuse(cable, nodesById, defLookup);
    if (fuse.ratingA == null || fuse.ratingA <= 0) continue;
    if (fuse.source.rating === "battery" && fuse.holderRatingA == null) continue;
    for (const { nodeId, terminal } of seriesFuseTerminals(cable, nodesById, defLookup, cranking.has(cable.id))) {
      if (fuse.ratingA <= terminal.maxAmp) continue;
      const label = labelOf(system, defLookup, nodeId);
      const vars = {
        label,
        terminal: terminal.label,
        maxAmp: terminal.maxAmp,
        fuse: fuse.ratingA,
      };
      issues.push({
        code: "fuse-exceeds-terminal",
        severity: "warning",
        title: { key: "diag.fuse-exceeds-terminal.title", vars },
        message: { key: "diag.fuse-exceeds-terminal.message", vars },
        nodeIds: [nodeId],
        cableIds: [cable.id],
        fix: { key: "diag.fuse-exceeds-terminal.fix", vars },
      });
    }
  }
  return issues;
}

/**
 * A cable end names a terminal its node does not have — a stud past the bus
 * bar's `studCount`, a zone, a node that is gone. The graph builder skips the
 * vertex, so everything past the cable is invisible to sizing and the energy
 * budget while the drawing still shows it wired.
 */
export function detectCableDanglingTerminal(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const endpointStatus = cableEndpointStatus(system, defLookup);
  const issues: Issue[] = [];
  for (const cable of system.cables) {
    for (const end of cableEnds(cable)) {
      const node = findNode(system, end.nodeId);
      const status = endpointStatus(end.nodeId, end.terminalId);
      if (status === "valid" || status === "unknown-definition") continue;
      const missingNode = status === "missing-node";
      const label = labelOf(system, defLookup, end.nodeId);
      const cableLabel =
        cable.label ??
        `${labelOf(system, defLookup, cable.fromNodeId)} → ${labelOf(system, defLookup, cable.toNodeId)}`;
      const vars = { cableLabel, label, terminal: end.terminalId };
      // A deleted component cannot be focused. Show its orphan cable on the
      // surviving component instead, where the user can actually find it.
      const nodeIds = node?.kind === "component" ? [node.id] : cableEnds(cable)
        .map(e => findNode(system, e.nodeId))
        .filter(n => n?.kind === "component")
        .map(n => n!.id);
      issues.push({
        code: "cable-dangling-terminal",
        severity: "critical",
        title: { key: missingNode ? "diag.cable-dangling-terminal.title_missing_node" : "diag.cable-dangling-terminal.title", vars },
        message: { key: missingNode ? "diag.cable-dangling-terminal.message_missing_node" : "diag.cable-dangling-terminal.message", vars },
        nodeIds: nodeIds.length ? [...new Set(nodeIds)] : undefined,
        cableIds: [cable.id],
        fix: { key: missingNode ? "diag.cable-dangling-terminal.fix_missing_node" : "diag.cable-dangling-terminal.fix", vars },
        fixAction: missingNode ? {
          kind: "delete-cables", cableIds: [cable.id], label: { key: "diag.cable-dangling-terminal.action_remove" },
        } : undefined,
      });
    }
  }
  return issues;
}

export function runTerminalDiagnostics(
  system: System,
  defLookup: DefLookup,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): Issue[] {
  return [
    ...detectCableDanglingTerminal(system, defLookup),
    ...detectTerminalOverloaded(system, defLookup, autoSizeTargetPct),
    ...detectFuseExceedsTerminal(system, defLookup),
  ];
}
